import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import OktaAuth from '@okta/okta-auth-js';
import { Security } from '@okta/okta-react';
import { Outlet } from '@rocketlane/rocket-router';
import React, { memo, Suspense, useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch } from 'react-redux';
import { PageLoading } from 'components/atoms/loading';
import { TokenErrorStatus } from 'components/organisms/token-error/constant';
import { CONFIG } from 'config/okta';
import { RoutePaths } from 'constant';
import { setQueryClientAction } from 'data/core/actions';
import { queryClient, useAccountMetaQuery } from 'queries';
import { MobileViewOverlay } from 'routes/mobile-view-overlay';
import { getOkta, logger, setOkta } from 'services';
// Keep TokenErrorStatus as regular import since it's an enum/type
const AccountNotFound = /*#__PURE__*/ React.lazy(()=>import('components/organisms/account').then((d)=>({
            default: d.AccountNotFound
        })));
const TokenError = /*#__PURE__*/ React.lazy(()=>import('components/organisms/token-error/token-error').then((d)=>({
            default: d.TokenError
        })));
export const SetupOkta = /*#__PURE__*/ memo(()=>{
    const dispatch = useDispatch();
    const { pathname } = window.location;
    const [authSetupComplete, setAuthSetupComplete] = useState(false);
    useEffect(()=>{
        dispatch(setQueryClientAction(queryClient));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        dispatch,
        queryClient
    ]);
    const { data, isLoading } = useAccountMetaQuery({
        onSuccess: async ({ okta: { clientId, issuerDomain } })=>{
            // Guard: only create the OktaAuth instance once. In React 18 StrictMode
            // effects (and their cleanups) run twice, which would call onSuccess again
            // with a fresh OktaAuth object — passing a new reference to <Security>
            // causes it to re-initialize, resetting authState and unmounting the tree.
            if (getOkta() !== null) {
                return;
            }
            // const oktaConfig = CONFIG(issuerDomain, clientId);
            // const authInstance = new OktaAuth(oktaConfig);
            // try {
            //   await AccountAPI.getInitData();
            //   authService.setAuthState(true);
            // } catch (error) {
            //   logger.error('Init call failed', { error: error as Error });
            //   authService.setAuthState(false);
            // }
            if (clientId && issuerDomain) {
                const oktaConfig = CONFIG(issuerDomain, clientId);
                const authInstance = new OktaAuth(oktaConfig);
                setOkta(authInstance);
                authInstance.authStateManager.updateAuthState();
            }
            setAuthSetupComplete(true);
        },
        onError: (err)=>{
            logger.error('Account meta failed', {
                error: err
            });
            setAuthSetupComplete(true);
        }
    });
    const oktaAuth = getOkta();
    const isAccountActionPage = pathname === RoutePaths.activate;
    // Stable callback — prevents Security from re-registering on every render
    const stableRestoreOriginalUri = useCallback(()=>{}, []);
    const componentToRender = ()=>{
        if (isLoading || !authSetupComplete) {
            return /*#__PURE__*/ _jsx(PageLoading, {});
        }
        if (data) {
            var _data_company, _data_company1, _data_company2, _data_company3;
            const appContent = /*#__PURE__*/ _jsxs(Suspense, {
                fallback: /*#__PURE__*/ _jsx(PageLoading, {}),
                children: [
                    /*#__PURE__*/ _jsxs(Helmet, {
                        titleTemplate: `%s - ${(_data_company = data.company) === null || _data_company === void 0 ? void 0 : _data_company.companyName}`,
                        defaultTitle: (_data_company1 = data.company) === null || _data_company1 === void 0 ? void 0 : _data_company1.companyName,
                        children: [
                            /*#__PURE__*/ _jsx("link", {
                                rel: "icon",
                                type: "image/png",
                                href: (_data_company2 = data.company) === null || _data_company2 === void 0 ? void 0 : _data_company2.companyLogoUrl
                            }),
                            /*#__PURE__*/ _jsx("link", {
                                rel: "icon",
                                type: "image/png",
                                sizes: "16x16",
                                href: (_data_company3 = data.company) === null || _data_company3 === void 0 ? void 0 : _data_company3.companyLogoUrl
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Outlet, {})
                ]
            });
            if (oktaAuth) {
                return /*#__PURE__*/ _jsx(Security, {
                    oktaAuth: oktaAuth,
                    restoreOriginalUri: stableRestoreOriginalUri,
                    children: appContent
                });
            }
            // Cookies flow: no Okta setup needed, render directly
            return appContent;
        }
        if (isAccountActionPage) {
            return /*#__PURE__*/ _jsx(Suspense, {
                fallback: /*#__PURE__*/ _jsx(PageLoading, {}),
                children: /*#__PURE__*/ _jsx(TokenError, {
                    status: TokenErrorStatus.AlreadyCompleted
                })
            });
        }
        return /*#__PURE__*/ _jsx(Suspense, {
            fallback: /*#__PURE__*/ _jsx(PageLoading, {}),
            children: /*#__PURE__*/ _jsx(AccountNotFound, {})
        });
    };
    return /*#__PURE__*/ _jsx(MobileViewOverlay, {
        children: componentToRender()
    });
});
SetupOkta.displayName = 'SetupOkta';
